import { eventBus, EventTypes, createEventPayload } from './EventBus';
import { DataValidator, type Project, type Ticket, type User } from './DataValidation';

export interface SearchResult {
  id: string;
  type: 'project' | 'ticket' | 'user' | 'document' | 'workflow';
  title: string;
  description: string;
  source: 'control-bridge' | 'support-bridge' | 'cross-system';
  relevance: number;
  metadata: Record<string, any>;
  url?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface SearchQuery {
  query: string;
  filters?: {
    type?: string[];
    source?: string[];
    dateRange?: {
      start: Date;
      end: Date;
    };
    status?: string[];
    priority?: string[];
    tags?: string[];
  };
  sortBy?: 'relevance' | 'date' | 'priority' | 'status';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface SearchIndex {
  id: string;
  type: string;
  title: string;
  description: string;
  content: string;
  tags: string[];
  metadata: Record<string, any>;
  source: string;
  lastIndexed: Date;
}

export class UnifiedSearchService {
  private static instance: UnifiedSearchService;
  private searchIndex: Map<string, SearchIndex> = new Map();
  private searchHistory: SearchQuery[] = [];
  private maxHistorySize: number = 100;

  private constructor() {
    this.initializeEventListeners();
  }

  static getInstance(): UnifiedSearchService {
    if (!UnifiedSearchService.instance) {
      UnifiedSearchService.instance = new UnifiedSearchService();
    }
    return UnifiedSearchService.instance;
  }

  // Initialize event listeners for real-time indexing
  private initializeEventListeners(): void {
    // Listen for project events
    eventBus.subscribe(EventTypes.PROJECT_CREATED, (payload) => {
      this.indexProject(payload.data);
    });

    eventBus.subscribe(EventTypes.PROJECT_UPDATED, (payload) => {
      this.updateProjectIndex(payload.data);
    });

    // Listen for support events
    eventBus.subscribe(EventTypes.TICKET_CREATED, (payload) => {
      this.indexTicket(payload.data);
    });

    eventBus.subscribe(EventTypes.TICKET_UPDATED, (payload) => {
      this.updateTicketIndex(payload.data);
    });

    // Listen for user events
    eventBus.subscribe(EventTypes.USER_ACTIVITY, (payload) => {
      this.indexUserActivity(payload.data);
    });
  }

  // Index a project
  private indexProject(project: any): void {
    try {
      const validatedProject = DataValidator.validateProject(project);
      
      const searchIndex: SearchIndex = {
        id: validatedProject.id,
        type: 'project',
        title: validatedProject.name,
        description: validatedProject.description || '',
        content: this.generateSearchContent(validatedProject),
        tags: [
          validatedProject.status,
          validatedProject.health,
          validatedProject.priority,
          validatedProject.clientName,
          ...validatedProject.timeline.milestones.map((m: any) => m.name)
        ].filter(Boolean),
        metadata: {
          status: validatedProject.status,
          health: validatedProject.health,
          priority: validatedProject.priority,
          progress: validatedProject.progress,
          budget: validatedProject.budget,
          teamSize: validatedProject.team.members,
          clientId: validatedProject.clientId,
          riskScore: validatedProject.riskScore
        },
        source: 'control-bridge',
        lastIndexed: new Date()
      };

      this.searchIndex.set(validatedProject.id, searchIndex);
      console.log(`[UnifiedSearch] Indexed project: ${validatedProject.name}`);
    } catch (error) {
      console.error(`[UnifiedSearch] Failed to index project:`, error);
    }
  }

  // Update project index
  private updateProjectIndex(project: any): void {
    this.indexProject(project);
  }

  // Index a support ticket
  private indexTicket(ticket: any): void {
    try {
      const validatedTicket = DataValidator.validateTicket(ticket);
      
      const searchIndex: SearchIndex = {
        id: validatedTicket.id,
        type: 'ticket',
        title: validatedTicket.title,
        description: validatedTicket.description,
        content: this.generateSearchContent(validatedTicket),
        tags: [
          validatedTicket.status,
          validatedTicket.priority,
          validatedTicket.clientTier,
          validatedTicket.frustrationLevel,
          validatedTicket.systemImpact,
          ...validatedTicket.tags
        ].filter(Boolean),
        metadata: {
          status: validatedTicket.status,
          priority: validatedTicket.priority,
          clientTier: validatedTicket.clientTier,
          escalationLevel: validatedTicket.escalationLevel,
          businessCritical: validatedTicket.businessCritical,
          projectId: validatedTicket.projectId,
          assignedAnalyst: validatedTicket.assignedAnalyst
        },
        source: 'support-bridge',
        lastIndexed: new Date()
      };

      this.searchIndex.set(validatedTicket.id, searchIndex);
      console.log(`[UnifiedSearch] Indexed ticket: ${validatedTicket.title}`);
    } catch (error) {
      console.error(`[UnifiedSearch] Failed to index ticket:`, error);
    }
  }

  // Update ticket index
  private updateTicketIndex(ticket: any): void {
    this.indexTicket(ticket);
  }

  // Index user activity
  private indexUserActivity(activity: any): void {
    const searchIndex: SearchIndex = {
      id: `activity_${activity.id}`,
      type: 'user',
      title: `User Activity: ${activity.userName}`,
      description: activity.description || '',
      content: this.generateSearchContent(activity),
      tags: [activity.type, activity.module, activity.action],
      metadata: {
        userId: activity.userId,
        userName: activity.userName,
        module: activity.module,
        action: activity.action,
        timestamp: activity.timestamp
      },
      source: 'cross-system',
      lastIndexed: new Date()
    };

    this.searchIndex.set(searchIndex.id, searchIndex);
  }

  // Generate searchable content from entities
  private generateSearchContent(entity: any): string {
    const contentParts: string[] = [];
    
    // Add all string fields
    Object.entries(entity).forEach(([key, value]) => {
      if (typeof value === 'string' && value) {
        contentParts.push(value);
      } else if (Array.isArray(value)) {
        value.forEach(item => {
          if (typeof item === 'string') {
            contentParts.push(item);
          } else if (typeof item === 'object' && item.name) {
            contentParts.push(item.name);
          }
        });
      } else if (typeof value === 'object' && value && typeof value.name === 'string') {
        contentParts.push(value.name);
      }
    });
    
    return contentParts.join(' ').toLowerCase();
  }

  // Perform unified search across all systems
  async search(query: SearchQuery): Promise<SearchResult[]> {
    const { query: searchQuery, filters, sortBy = 'relevance', sortOrder = 'desc', limit = 50, offset = 0 } = query;
    
    // Store search in history
    this.searchHistory.push(query);
    if (this.searchHistory.length > this.maxHistorySize) {
      this.searchHistory.shift();
    }

    // Perform search across all indexed items
    const results: SearchResult[] = [];
    
    for (const [id, index] of this.searchIndex.entries()) {
      if (this.matchesQuery(index, searchQuery, filters)) {
        const relevance = this.calculateRelevance(index, searchQuery);
        
        results.push({
          id: index.id,
          type: index.type as any,
          title: index.title,
          description: index.description,
          source: index.source as any,
          relevance,
          metadata: index.metadata,
          url: this.generateUrl(index),
          createdAt: index.lastIndexed,
          updatedAt: index.lastIndexed
        });
      }
    }

    // Sort results
    results.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'relevance':
          comparison = b.relevance - a.relevance;
          break;
        case 'date':
          comparison = new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
          break;
        case 'priority':
          comparison = this.comparePriority(a.metadata, b.metadata);
          break;
        case 'status':
          comparison = a.metadata.status?.localeCompare(b.metadata.status || '') || 0;
          break;
      }
      
      return sortOrder === 'desc' ? comparison : -comparison;
    });

    // Apply pagination
    const paginatedResults = results.slice(offset, offset + limit);

    // Publish search event
    eventBus.publish(EventTypes.USER_ACTIVITY, createEventPayload(
      'unified-search',
      {
        query: searchQuery,
        resultCount: results.length,
        filteredCount: paginatedResults.length
      }
    ));

    return paginatedResults;
  }

  // Check if index matches search query and filters
  private matchesQuery(index: SearchIndex, query: string, filters?: any): boolean {
    // Text search
    const searchText = query.toLowerCase();
    const content = index.content.toLowerCase();
    const title = index.title.toLowerCase();
    const description = index.description.toLowerCase();
    
    const textMatch = content.includes(searchText) || 
                     title.includes(searchText) || 
                     description.includes(searchText) ||
                     index.tags.some(tag => tag.toLowerCase().includes(searchText));

    if (!textMatch) return false;

    // Apply filters
    if (filters) {
      if (filters.type && !filters.type.includes(index.type)) return false;
      if (filters.source && !filters.source.includes(index.source)) return false;
      if (filters.status && !filters.status.includes(index.metadata.status)) return false;
      if (filters.priority && !filters.priority.includes(index.metadata.priority)) return false;
      if (filters.tags && !filters.tags.some((tag: string) => index.tags.includes(tag))) return false;
      
      if (filters.dateRange) {
        const lastIndexed = new Date(index.lastIndexed);
        if (lastIndexed < filters.dateRange.start || lastIndexed > filters.dateRange.end) return false;
      }
    }

    return true;
  }

  // Calculate search relevance score
  private calculateRelevance(index: SearchIndex, query: string): number {
    const searchText = query.toLowerCase();
    let score = 0;

    // Title match (highest weight)
    if (index.title.toLowerCase().includes(searchText)) {
      score += 10;
    }

    // Exact title match
    if (index.title.toLowerCase() === searchText) {
      score += 20;
    }

    // Description match
    if (index.description.toLowerCase().includes(searchText)) {
      score += 5;
    }

    // Content match
    if (index.content.toLowerCase().includes(searchText)) {
      score += 3;
    }

    // Tag matches
    const tagMatches = index.tags.filter(tag => 
      tag.toLowerCase().includes(searchText)
    ).length;
    score += tagMatches * 2;

    // Recency bonus
    const daysSinceIndexed = (Date.now() - index.lastIndexed.getTime()) / (1000 * 60 * 60 * 24);
    if (daysSinceIndexed < 1) score += 3;
    else if (daysSinceIndexed < 7) score += 2;
    else if (daysSinceIndexed < 30) score += 1;

    return Math.min(score, 100);
  }

  // Compare priority for sorting
  private comparePriority(a: any, b: any): number {
    const priorityOrder = { 'critical': 4, 'high': 3, 'medium': 2, 'low': 1, 'urgent': 5 };
    const priorityA = priorityOrder[a.priority] || 0;
    const priorityB = priorityOrder[b.priority] || 0;
    return priorityB - priorityA;
  }

  // Generate URL for search result
  private generateUrl(index: SearchIndex): string {
    switch (index.type) {
      case 'project':
        return `/control-bridge/projects/${index.id}`;
      case 'ticket':
        return `/support-bridge/tickets/${index.id}`;
      case 'user':
        return `/users/${index.metadata.userId}`;
      case 'workflow':
        return `/workflows/${index.id}`;
      default:
        return `/${index.source}/${index.type}/${index.id}`;
    }
  }

  // Get search suggestions
  getSearchSuggestions(query: string, limit: number = 5): string[] {
    const suggestions = new Set<string>();
    const searchText = query.toLowerCase();

    for (const index of this.searchIndex.values()) {
      // Title suggestions
      if (index.title.toLowerCase().includes(searchText)) {
        suggestions.add(index.title);
      }

      // Tag suggestions
      index.tags.forEach(tag => {
        if (tag.toLowerCase().includes(searchText)) {
          suggestions.add(tag);
        }
      });

      if (suggestions.size >= limit) break;
    }

    return Array.from(suggestions).slice(0, limit);
  }

  // Get search history
  getSearchHistory(): SearchQuery[] {
    return [...this.searchHistory].reverse();
  }

  // Clear search history
  clearSearchHistory(): void {
    this.searchHistory = [];
  }

  // Get search statistics
  getSearchStats(): Record<string, number> {
    const stats: Record<string, number> = {};
    
    for (const index of this.searchIndex.values()) {
      stats[index.type] = (stats[index.type] || 0) + 1;
      stats[index.source] = (stats[index.source] || 0) + 1;
    }
    
    return stats;
  }

  // Rebuild search index
  async rebuildIndex(): Promise<void> {
    console.log('[UnifiedSearch] Rebuilding search index...');
    this.searchIndex.clear();
    
    // This would typically fetch data from various systems
    // For now, we'll just log the action
    console.log('[UnifiedSearch] Search index rebuilt');
  }

  // Remove item from index
  removeFromIndex(id: string): boolean {
    return this.searchIndex.delete(id);
  }

  // Get indexed item
  getIndexedItem(id: string): SearchIndex | undefined {
    return this.searchIndex.get(id);
  }

  // Get all indexed items
  getAllIndexedItems(): SearchIndex[] {
    return Array.from(this.searchIndex.values());
  }
}

// Export singleton instance
export const unifiedSearchService = UnifiedSearchService.getInstance();

// Search result type
export type SearchResultType = SearchResult['type'];
export type SearchSource = SearchResult['source'];